import {
  EntityTypeEnum,
  NegotiationEventCancelBulk,
  NegotiationEventUpdateAttributesBulk,
  NegotiationEventUpdatePreAssignmentAttributes,
  NegotiationEventUpdateRegulatoryAttributes,
  NegotiationSubscriptionIdentifier,
  NegotiationUpdateAttributes,
  NegotiationUpdateAttributesMeta,
  SubscribeType,
  SubscriptionFilter
} from 'api'
import { PostTransformSubscriptionFilters } from 'model'
import { getCurrentWindowId } from 'utils'
import { getPageSize } from 'utils/pageSizeUtils'
import { clientActionEvent, clientActionSubscribe } from './ClientAction'
import { EntityType } from './EntityType'
import {
  NegotiationEventAccept,
  NegotiationEventAcceptBulk,
  NegotiationEventAcceptBulkNegotiation,
  NegotiationEventBackToStaging,
  NegotiationEventCancel,
  NegotiationEventCounterOffer,
  NegotiationEventInitiatorRevertAttributes,
  NegotiationEventLock,
  NegotiationEventQuote,
  NegotiationEventReject,
  NegotiationEventResponderRevertAttributes,
  NegotiationEventUnlock,
  NegotiationEventUpdateAttributes,
  NegotiationEventWithdraw,
  NegotiationFieldMeta,
  NegotiationSide
} from './generated'

export const ownOrderToSide = (ownOrder: boolean) => (ownOrder ? NegotiationSide.INITIATOR : NegotiationSide.RESPONDER)

function negotiationLock(negotiationId: string, ownOrder: boolean, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<NegotiationEventLock>(
    'NegotiationEventLock',
    { negotiationId, negotiationSide: ownOrderToSide(ownOrder) },
    sourceWindowId,
    {
      entityIds: [negotiationId],
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationUnlock(negotiationId: string, ownOrder: boolean, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<NegotiationEventUnlock>(
    'NegotiationEventUnlock',
    { negotiationId, negotiationSide: ownOrderToSide(ownOrder) },
    sourceWindowId,
    {
      entityIds: [negotiationId],
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationAccept(negotiationId: string, ownOrder: boolean, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<NegotiationEventAccept>(
    'NegotiationEventAccept',
    {
      negotiationId,
      negotiationSide: ownOrderToSide(ownOrder),
      quoteResponseId: negotiationId
    },
    sourceWindowId,
    {
      entityIds: [negotiationId],
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationAcceptBulk(
  negotiationEventAcceptBulkNegotiations: NegotiationEventAcceptBulkNegotiation[],
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<NegotiationEventAcceptBulk>(
    'NegotiationEventAcceptBulk',
    {
      negotiationEventAcceptBulkNegotiations
    },
    sourceWindowId,
    {
      entityIds: negotiationEventAcceptBulkNegotiations.map(negotiation => negotiation.negotiationId),
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationInitiatorRevert(negotiationId: string, ownOrder: boolean, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<NegotiationEventInitiatorRevertAttributes>(
    'NegotiationEventInitiatorRevertAttributes',
    { negotiationId, negotiationSide: ownOrderToSide(ownOrder) },
    sourceWindowId,
    { entityIds: [negotiationId], entityType: EntityType.NEGOTIATION }
  )
}

function negotiationResponderRevert(negotiationId: string, ownOrder: boolean, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<NegotiationEventResponderRevertAttributes>(
    'NegotiationEventResponderRevertAttributes',
    { negotiationId, negotiationSide: ownOrderToSide(ownOrder) },
    sourceWindowId,
    { entityIds: [negotiationId], entityType: EntityType.NEGOTIATION }
  )
}

function negotiationUpdateValues(
  negotiationId: string,
  ownOrder: boolean,
  updatedValues: { [P in NegotiationFieldMeta]?: any },
  updatedRawValues: { [P in NegotiationFieldMeta]?: any } = {},
  meta: NegotiationUpdateAttributesMeta | null = null,
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<NegotiationEventUpdateAttributes>(
    'NegotiationEventUpdateAttributes',
    {
      negotiationId,
      negotiationSide: ownOrderToSide(ownOrder),
      updatedValues,
      updatedRawValues,
      meta
    },
    sourceWindowId,
    { entityIds: [negotiationId], entityType: EntityType.NEGOTIATION }
  )
}

function negotiationUpdateValuesBulk(
  negotiationUpdateAttributesList: NegotiationUpdateAttributes[],
  ownOrder: boolean,
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<NegotiationEventUpdateAttributesBulk>(
    'NegotiationEventUpdateAttributesBulk',
    {
      negotiationUpdateAttributesList,
      negotiationSide: ownOrderToSide(ownOrder)
    },
    sourceWindowId,
    {
      entityIds: negotiationUpdateAttributesList.map(attributesList => attributesList.negotiationId),
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationUpdateRegulatoryValues(
  negotiationId: string,
  investmentDecisionMaker: string | null,
  executionDecisionMaker: string | null,
  ownOrder: boolean,
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<NegotiationEventUpdateRegulatoryAttributes>(
    'NegotiationEventUpdateRegulatoryAttributes',
    {
      negotiationId,
      investmentDecisionMaker,
      executionDecisionMaker,
      negotiationSide: ownOrderToSide(ownOrder)
    },
    sourceWindowId,
    { entityIds: [negotiationId], entityType: EntityType.NEGOTIATION }
  )
}

function negotiationQuote(
  negotiationId: string,
  ownOrder: boolean,
  otwValidUntil: string | null,
  isLastLook: boolean,
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<NegotiationEventQuote>(
    'NegotiationEventQuote',
    {
      negotiationId,
      negotiationSide: ownOrderToSide(ownOrder),
      validUntilTime: otwValidUntil,
      isLastLook: isLastLook
    },
    sourceWindowId,
    {
      entityIds: [negotiationId],
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationCounter(
  negotiationId: string,
  ownOrder: boolean,
  refresh: boolean = false,
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<NegotiationEventCounterOffer>(
    'NegotiationEventCounterOffer',
    { negotiationId, negotiationSide: ownOrderToSide(ownOrder), refresh },
    sourceWindowId,
    {
      entityIds: [negotiationId],
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationWithdraw(negotiationId: string, ownOrder: boolean, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<NegotiationEventWithdraw>(
    'NegotiationEventWithdraw',
    { negotiationId, negotiationSide: ownOrderToSide(ownOrder) },
    sourceWindowId,
    {
      entityIds: [negotiationId],
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationReject(negotiationId: string, ownOrder: boolean, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<NegotiationEventReject>(
    'NegotiationEventReject',
    {
      negotiationId,
      negotiationSide: ownOrderToSide(ownOrder),
      externalQuoteResponseId: null
    },
    sourceWindowId,
    {
      entityIds: [negotiationId],
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationCancel(negotiationId: string, ownOrder: boolean, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<NegotiationEventCancel>(
    'NegotiationEventCancel',
    { negotiationId, negotiationSide: ownOrderToSide(ownOrder), quoteResponseId: null },
    sourceWindowId,
    {
      entityIds: [negotiationId],
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationCancelBulk(negotiationIds: string[], cloneOriginalOrder: boolean, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<NegotiationEventCancelBulk>(
    'NegotiationEventCancelBulk',
    { negotiationIds, negotiationSide: NegotiationSide.INITIATOR, cloneOriginalOrder },
    sourceWindowId,
    {
      entityIds: negotiationIds,
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationBackToStaging(negotiationId: string, ownOrder: boolean, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<NegotiationEventBackToStaging>(
    'NegotiationEventBackToStaging',
    { negotiationId, negotiationSide: ownOrderToSide(ownOrder), collectionId: null },
    sourceWindowId,
    {
      entityIds: [negotiationId],
      entityType: EntityType.NEGOTIATION
    }
  )
}

function negotiationPageSubscribe(
  subscriptionIdentifier: NegotiationSubscriptionIdentifier,
  filters: SubscriptionFilter[],
  pageNumber: number = 0
) {
  return clientActionSubscribe({
    entityTypeEnum: EntityTypeEnum.NEGOTIATION,
    preDefinedFilter: null,
    filters,
    postTransformFilters: PostTransformSubscriptionFilters[subscriptionIdentifier],
    subscribeType: SubscribeType.PAGE,
    subscriptionIdentifier,
    limitResults: false,
    pageSize: getPageSize(subscriptionIdentifier),
    pageNumber,
    summaryRequest: null
  })
}

function negotiationLiveOnlySubscribe(subscriptionIdentifier: NegotiationSubscriptionIdentifier, filters: SubscriptionFilter[]) {
  return clientActionSubscribe({
    entityTypeEnum: EntityTypeEnum.NEGOTIATION,
    preDefinedFilter: null,
    filters,
    postTransformFilters: PostTransformSubscriptionFilters[subscriptionIdentifier],
    subscribeType: SubscribeType.LIVE_ONLY,
    subscriptionIdentifier,
    limitResults: false,
    pageSize: null,
    pageNumber: null,
    summaryRequest: null
  })
}

export type NegotiationUpdatePreAssignmentAttributesArgs = Omit<
  NegotiationEventUpdatePreAssignmentAttributes,
  'eventType' | 'correlationId'
>

function negotiationUpdatePreAssignmentAttributes(
  args: NegotiationUpdatePreAssignmentAttributesArgs,
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent('NegotiationEventUpdatePreAssignmentAttributes', args, sourceWindowId, {
    entityIds: [args.negotiationId],
    entityType: EntityType.NEGOTIATION
  })
}

export const NegotiationActions = {
  lock: negotiationLock,
  unlock: negotiationUnlock,
  accept: negotiationAccept,
  acceptBulk: negotiationAcceptBulk,
  initiatorRevert: negotiationInitiatorRevert,
  responderRevert: negotiationResponderRevert,
  updateValues: negotiationUpdateValues,
  updateValuesBulk: negotiationUpdateValuesBulk,
  updateRegulatoryValues: negotiationUpdateRegulatoryValues,
  quote: negotiationQuote,
  counter: negotiationCounter,
  withdraw: negotiationWithdraw,
  reject: negotiationReject,
  cancel: negotiationCancel,
  cancelBulk: negotiationCancelBulk,
  backToStaging: negotiationBackToStaging,
  pageSubscribe: negotiationPageSubscribe,
  liveOnlySubscribe: negotiationLiveOnlySubscribe,
  updatePreAssignment: negotiationUpdatePreAssignmentAttributes
}
